import { useMemo, useState, useCallback } from "react";
import { ArrowUp, ArrowDown, Trash2, Replace, Plus } from "lucide-react";
import { RichTextEditor } from "../../RichTextEditor";
import { ContentBlock } from "./ContentBlock";
import { ContentPicker } from "./ContentPicker";
import {
  splitContent,
  blockMarker,
  type CommunityContentBlock,
  type ContentKind,
} from "../../../lib/communityContent";
export function CommunityBodyEditor({
  value,
  onChange,
  blocks,
  onBlocksChange,
  initial,
  onInitialUsed,
}: {
  value: string;
  onChange: (html: string) => void;
  blocks: CommunityContentBlock[];
  onBlocksChange: (blocks: CommunityContentBlock[]) => void;
  initial?: { kind: ContentKind; sourceId?: string };
  onInitialUsed: () => void;
}) {
  const parts = useMemo(() => splitContent(value), [value]);
  const [target, setTarget] = useState<{
    index: number;
    before?: string;
    after?: string;
    replaceId?: string;
  } | null>(null);
  const close = useCallback(() => {
    setTarget(null);
    onInitialUsed();
  }, [onInitialUsed]);
  const pickerOpen = Boolean(target || initial);
  function insert(b: CommunityContentBlock) {
    const position = target || { index: parts.length };
    const next = [...parts];
    const replace = position.replaceId;
    const block = replace ? { ...b, id: replace } : b;
    onBlocksChange([...blocks.filter((x) => x.id !== block.id), block]);
    const marker = {
      type: "block" as const,
      id: block.id,
      html: blockMarker(block),
    };
    if (replace) next.splice(position.index, 1, marker);
    else if (position.before !== undefined)
      next.splice(
        position.index,
        1,
        { type: "text", html: position.before },
        marker,
        { type: "text", html: position.after || "<p><br></p>" },
      );
    else
      next.splice(position.index, 0, marker, {
        type: "text",
        html: "<p><br></p>",
      });
    onChange(next.map((p) => p.html).join(""));
    close();
  }
  function move(index: number, offset: number) {
    const next = [...parts];
    const to = index + offset;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to], next[index]];
    onChange(next.map((p) => p.html).join(""));
  }
  return (
    <div className="fe-content-editor">
      <div className="fe-content-editor-intro">
        <span>글과 내 콘텐츠를 함께 담아보세요</span>
        <button
          type="button"
          className="fe-community-button"
          onClick={() => setTarget({ index: parts.length })}
        >
          <Plus size={16} />내 콘텐츠 넣기
        </button>
      </div>
      {parts.map((p, i) => {
        if (p.type === "text")
          return (
            <RichTextEditor
              key={"text" + i}
              value={p.html}
              onChange={(html) => {
                const next = [...parts];
                next[i] = { type: "text", html };
                onChange(next.map((x) => x.html).join(""));
              }}
              compact={parts.length > 1}
              onInsertContent={(before, after) =>
                setTarget({ index: i, before, after })
              }
            />
          );
        const b = blocks.find((b) => b.id === p.id);
        return (
          <div className="fe-content-editor-block" key={p.id}>
            <div className="fe-content-block-controls">
              <span>본문에 연결된 콘텐츠</span>
              <button
                type="button"
                aria-label="콘텐츠 앞으로 이동"
                disabled={i === 0}
                onClick={() => move(i, -1)}
              >
                <ArrowUp size={16} />
              </button>
              <button
                type="button"
                aria-label="콘텐츠 뒤로 이동"
                disabled={i === parts.length - 1}
                onClick={() => move(i, 1)}
              >
                <ArrowDown size={16} />
              </button>
              <button
                type="button"
                aria-label="콘텐츠 교체"
                onClick={() => setTarget({ index: i, replaceId: p.id })}
              >
                <Replace size={16} />
              </button>
              <button
                type="button"
                aria-label="콘텐츠 제거"
                onClick={() => {
                  onChange(
                    parts
                      .filter((_, j) => j !== i)
                      .map((x) => x.html)
                      .join(""),
                  );
                  onBlocksChange(blocks.filter((b) => b.id !== p.id));
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>
            {b ? (
              <ContentBlock block={b} preview />
            ) : (
              <p>
                이 콘텐츠를 확인할 수 없습니다. 다시 선택하거나 제거해주세요.
              </p>
            )}
          </div>
        );
      })}
      {pickerOpen && (
        <ContentPicker
          onClose={close}
          onInsert={insert}
          initial={initial || undefined}
        />
      )}
    </div>
  );
}
